import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRightFromBracket,
  faMessage,
  faUsers,
  faPaperPlane,
  faExclamationCircle,
  faShare,
  faBars,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { Modal, Button } from "react-bootstrap";
import { io } from "socket.io-client";
import Toast from "../../Components/Toast";
import styles from "../../Styles/Community Forum/CommunityForumDashboard.module.css";
import gearsGif from "../../Assets/gears.gif";

export const CommunityForumDashboard = () => {
  const { mainCategoryId } = useParams();
  const navigate = useNavigate();

  // State management
  const [mainCategory, setMainCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [interestMap, setInterestMap] = useState({});
  const [memberCounts, setMemberCounts] = useState({});

  // Chat state
  const [messages, setMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareSearch, setShareSearch] = useState("");
  const [shareResults, setShareResults] = useState([]);
  const [selectedShareEntry, setSelectedShareEntry] = useState(null);
  const [shareSearchLoading, setShareSearchLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  
  // Mobile menu state
  const [showMobileTopics, setShowMobileTopics] = useState(false);

  const messagesEndRef = useRef(null);
  const fetchMainCategoryDataRef = useRef(null);
  const fetchInterestsRef = useRef(null);
  const loadMessagesRef = useRef(null);

  const socket = useMemo(() => {
    const url = process.env.REACT_APP_BACKEND_URL;
    if (!url) return null;
    let baseUrl = url;
    try {
      const parsed = new URL(url);
      baseUrl = parsed.origin;
    } catch (e) {
      const match = url.match(/^(https?:\/\/[^/]+)/);
      if (match && match[1]) {
        baseUrl = match[1];
      }
    }
    return io(baseUrl, { transports: ["websocket"] });
  }, []);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" }); // Changed from "smooth" to "auto"
    }
  };

  const activeInterestId = selectedTopic ? interestMap[selectedTopic.name?.toLowerCase()] : null;

  // Fetch main category and subcategories
  const fetchMainCategoryData = async () => {
    try {
      setLoading(true);
      const token = Cookies.get("adminToken");

      const subCatRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/sub-categories/${mainCategoryId}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      const subs = subCatRes.data?.data || [];
      setSubCategories(subs);

      const allCatsRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/main-categories`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      const allCats = allCatsRes.data?.data || [];
      const currentCat = allCats.find(cat => cat._id === mainCategoryId);
      setMainCategory(currentCat || { name: "Community Forum", _id: mainCategoryId });

      if (subs.length > 0) {
        setSelectedSubCategoryId(subs[0]._id);
        fetchTopicsForSubCategory(subs[0]._id);
      }
    } catch (error) {
      console.error("Error fetching main category:", error);
      toast.error("Failed to load category data");
    } finally {
      setLoading(false);
    }
  };

  const fetchInterests = async () => {
    try {
      const token = Cookies.get("adminToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/interests`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const list = response.data?.data || [];
      const map = {};
      list.forEach((interest) => {
        if (interest.name) {
          map[interest.name.toLowerCase()] = interest._id;
        }
      });
      setInterestMap(map);
      fetchMemberCounts(list);
    } catch (error) {
      console.error("interests fetching error", error);
    }
  };

  const fetchMemberCounts = async (interests) => {
    try {
      const token = Cookies.get("adminToken");
      const counts = {};
      
      await Promise.all(
        interests.map(async (interest) => {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/admin/interests/${interest._id}/user-count`,
              { headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );
            counts[interest._id] = response.data?.data?.userCount || 0;
          } catch (err) {
            counts[interest._id] = 0;
          }
        })
      );
      
      setMemberCounts(counts);
    } catch (error) {
      console.error("Error fetching member counts:", error);
    }
  };

  const fetchTopicsForSubCategory = async (subCategoryId) => {
    try {
      setLoading(true);
      const token = Cookies.get("adminToken");

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/topics/${mainCategoryId}/${subCategoryId}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      const topicsList = response.data?.data?.topics || [];
      setTopics(topicsList);

      if (topicsList.length > 0) {
        handleTopicSelect(topicsList[0]);
      } else {
        setSelectedTopic(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      toast.error("Failed to load topics");
      setTopics([]);
      setSelectedTopic(null);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (interestId, silent = false) => {
    if (!interestId) return;
    
    try {
      if (!silent) setLoadingMessages(true);
      const token = Cookies.get("adminToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/messages/${interestId}`,
        {
          params: { page: 1, limit: 200 },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      const list = response.data?.data?.messages || [];
      setMessages(list);
      
      // Scroll to bottom instantly after messages load
      if (!silent && list.length > 0) {
        setTimeout(scrollToBottom, 100);
      }
    } catch (error) {
      console.error("fetch messages error", error);
      if (!silent) {
        toast.error("Failed to load messages");
      }
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  };

  const handleSubCategoryChange = (subCategoryId) => {
    setSelectedSubCategoryId(subCategoryId);
    fetchTopicsForSubCategory(subCategoryId);
  };

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    const interestId = interestMap[topic.name?.toLowerCase()];
    if (interestId) {
      loadMessages(interestId);
    } else {
      setMessages([]);
    }
    // Close mobile menu after selection
    setShowMobileTopics(false);
  };

  const handleSendNormal = async () => {
    if (!chatText.trim()) {
      toast.error("Message is required");
      return;
    }
    if (!activeInterestId) {
      toast.error("No chat room selected");
      return;
    }
    try {
      setSendingMessage(true);
      const token = Cookies.get("adminToken");
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/message`,
        { interestId: activeInterestId, message: chatText.trim() },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setChatText("");
    } catch (error) {
      console.error("send message error", error);
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSendImportant = async () => {
    if (!chatText.trim()) {
      toast.error("Message is required");
      return;
    }
    if (!activeInterestId) {
      toast.error("No chat room selected");
      return;
    }
    try {
      setSendingMessage(true);
      const token = Cookies.get("adminToken");
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/message/important`,
        { interestId: activeInterestId, message: chatText.trim() },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setChatText("");
    } catch (error) {
      console.error("send important message error", error);
      toast.error("Failed to send important message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSearchChange = async (e) => {
    const value = e.target.value;
    setShareSearch(value);
    setShareResults([]);
    setSelectedShareEntry(null);
    if (!value || value.trim().length < 2) {
      setShareSearchLoading(false);
      return;
    }
    try {
      setShareSearchLoading(true);
      const token = Cookies.get("adminToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/search/cross-category`,
        {
          params: { query: value.trim(), limit: 10 },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      const results = response.data?.data?.results || [];
      setShareResults(results);
    } catch (error) {
      console.error("cross-category search error", error);
      toast.error("Failed to search content");
    } finally {
      setShareSearchLoading(false);
    }
  };

  const handleSelectShareEntry = (entry) => {
    setSelectedShareEntry(entry);
    setShareSearch("");
    setShareResults([]);
  };

  const handleSendShare = async () => {
    if (!activeInterestId || !selectedShareEntry) {
      toast.error("Please select a content item");
      return;
    }
    try {
      setShareLoading(true);
      const token = Cookies.get("adminToken");
      const payload = {
        interestId: activeInterestId,
        entryId: selectedShareEntry._id,
        mainCategoryId: selectedShareEntry.mainCategory?._id,
        subCategoryId: selectedShareEntry.subCategory?._id,
        topicId: selectedShareEntry.topic?._id,
      };
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/messages/admin-share-entry`,
        payload,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setShowShareModal(false);
      setShareSearch("");
      setShareResults([]);
      setSelectedShareEntry(null);
      toast.success("Content shared successfully");
    } catch (error) {
      console.error("share entry error", error);
      toast.error("Failed to share entry");
    } finally {
      setShareLoading(false);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!chatText.trim() || sendingMessage) return;
      handleSendNormal();
    }
  };

  const handleLogout = () => {
    Cookies.remove("adminToken");
    navigate("/admin/signin");
  };

  fetchMainCategoryDataRef.current = fetchMainCategoryData;
  fetchInterestsRef.current = fetchInterests;
  loadMessagesRef.current = loadMessages;

  useEffect(() => {
    if (mainCategoryId) {
      fetchMainCategoryDataRef.current?.();
      fetchInterestsRef.current?.();
    }
  }, [mainCategoryId]);

  useEffect(() => {
    if (!activeInterestId) return;
    const intervalId = setInterval(() => {
      loadMessagesRef.current?.(activeInterestId, true);
    }, 3000);
    return () => clearInterval(intervalId);
  }, [activeInterestId]);

  useEffect(() => {
    if (!socket || !activeInterestId) return;
    socket.emit("joinInterest", activeInterestId);
    return () => {
      socket.emit("leaveInterest", activeInterestId);
    };
  }, [socket, activeInterestId]);

  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (payload) => {
      if (payload?.message) {
        setMessages((prev) => [...prev, payload.message]);
      }
    };

    socket.on("newMessage", handleNewMessage);
    socket.on("newImportantMessage", handleNewMessage);
    socket.on("newSharedEntry", handleNewMessage);

    return () => {
      socket.off("newMessage", handleNewMessage);
      socket.off("newImportantMessage", handleNewMessage);
      socket.off("newSharedEntry", handleNewMessage);
    };
  }, [socket]);

  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(scrollToBottom, 50); // Reduced delay, instant scroll
    }
  }, [messages.length]);

  const getMemberCount = (topicName) => {
    if (!topicName) return 0;
    const key = topicName.toLowerCase();
    const interestId = interestMap[key];
    if (!interestId) return 0;
    return memberCounts[interestId] || 0;
  };

  const formatTime = (value) => {
    if (!value) return "";
    try {
      const date = new Date(value);
      return date.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "";
    }
  };

  const formatDate = (value) => {
    if (!value) return "";
    try {
      const date = new Date(value);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === today.toDateString()) {
        return "Today";
      } else if (date.toDateString() === yesterday.toDateString()) {
        return "Yesterday";
      } else {
        return date.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
    } catch (e) {
      return "";
    }
  };

  const formatSharedBody = (value) => {
    if (!value) return "";
    return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  };

  const handleSharedEntryClick = (sharedEntry) => {
    if (!sharedEntry) return;
    
    const { mainCategoryId, subCategoryId, topicId, entryId } = sharedEntry;
    
    if (mainCategoryId && subCategoryId && topicId && entryId) {
      // Navigate to the entry preview page
      navigate(
        `/admin/topic-entries/${mainCategoryId}/${subCategoryId}/${topicId}/preview/${entryId}${window.location.search}`
      );
    } else {
      toast.error("Unable to open content preview");
    }
  };

  return (
    <>
      <Toast />
      <div className={styles.pageContainer}>
        {/* Header with Subcategories */}
        <header className={styles.simpleHeader}>
          <div className={styles.headerTop}>
            <div className={styles.headerLeft}>
              <button
                className={styles.mobileMenuBtn}
                onClick={() => setShowMobileTopics(!showMobileTopics)}
                title="Toggle Chat Rooms"
              >
                <FontAwesomeIcon icon={faBars} />
                <span>Chat Rooms</span>
              </button>
              <button
                className={styles.backHomeBtn}
                onClick={() => navigate("/admin/dashboard")}
                title="Back to Home"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                <span>Back</span>
              </button>
            </div>
            <div className={styles.headerCenter}>
              <h1 className={styles.categoryTitle}>
                <FontAwesomeIcon icon={faMessage} className={styles.categoryIcon} />
                {mainCategory?.name || "Community Forum"}
              </h1>
            </div>
            <div className={styles.headerRight}>
              <button
                className={styles.logoutBtn}
                onClick={handleLogout}
                title="Logout"
              >
                <FontAwesomeIcon icon={faArrowRightFromBracket} />
                <span>Logout</span>
              </button>
            </div>
          </div>
          
          {/* Subcategories in Navbar */}
          <div className={styles.headerBottom}>
            <div className={styles.subcategoriesInNav}>
              {subCategories.map((subCat) => (
                <button
                  key={subCat._id}
                  className={`${styles.subcategoryBtn} ${
                    selectedSubCategoryId === subCat._id
                      ? styles.subcategoryBtnActive
                      : ""
                  }`}
                  onClick={() => handleSubCategoryChange(subCat._id)}
                >
                  {subCat.name}
                </button>
              ))}
            </div>
          </div>
        </header>

        <div className={styles.mainContentArea}>
          {/* Left Panel - Chat Rooms List */}
          <div className={`${styles.topicsPanel} ${showMobileTopics ? styles.topicsPanelOpen : ''}`}>
            <div className={styles.topicsPanelHeader}>
              <h3 className={styles.topicsPanelTitle}>
                <FontAwesomeIcon icon={faMessage} />
                Chat Rooms
              </h3>
              <button 
                className={styles.closeMobileTopics}
                onClick={() => setShowMobileTopics(false)}
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className={styles.topicsListContainer}>
              {loading && topics.length === 0 ? (
                <div className={styles.loadingState}>
                  <img src={gearsGif} alt="Loading" width="30" />
                  <p>Loading...</p>
                </div>
              ) : topics.length > 0 ? (
                topics.map((topic) => {
                  const memberCount = getMemberCount(topic.name);
                  const isActive = selectedTopic?._id === topic._id;
                  
                  return (
                    <div
                      key={topic._id}
                      className={`${styles.topicCard} ${
                        isActive ? styles.topicCardActive : ""
                      }`}
                      onClick={() => handleTopicSelect(topic)}
                    >
                      <div className={styles.topicInfo}>
                        <span className={styles.topicName}>{topic.name}</span>
                        <span className={styles.memberCount}>
                          <FontAwesomeIcon icon={faUsers} />
                          {memberCount} {memberCount === 1 ? 'member' : 'members'}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className={styles.emptyState}>
                  <p>No chat rooms</p>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Overlay */}
          {showMobileTopics && (
            <div 
              className={styles.mobileOverlay}
              onClick={() => setShowMobileTopics(false)}
            />
          )}

          {/* Right Panel - Active Chat */}
          <div className={styles.infoPanel}>
            {selectedTopic ? (
              <>
                <div className={styles.chatHeader}>
                  <div className={styles.chatHeaderInfo}>
                    <h3 className={styles.chatHeaderTitle}>{selectedTopic.name}</h3>
                    <span className={styles.chatHeaderMembers}>
                      <FontAwesomeIcon icon={faUsers} />
                      {getMemberCount(selectedTopic.name)} members
                    </span>
                  </div>
                </div>

                <div className={styles.messagesArea}>
                  {loadingMessages && messages.length === 0 ? (
                    <div className={styles.loadingState}>
                      <img src={gearsGif} alt="Loading" width="40" />
                      <p>Loading messages...</p>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className={styles.emptyState}>
                      <p>No messages yet</p>
                      <p className={styles.emptySubtext}>Start the conversation!</p>
                    </div>
                  ) : (
                    <div className={styles.messagesList}>
                      {messages.map((message, index) => {
                        const currentDate = formatDate(message.createdAt);
                        const previousDate = index > 0 ? formatDate(messages[index - 1].createdAt) : null;
                        const showDateDivider = !previousDate || previousDate !== currentDate;

                        const isAdmin = !!message.isAdminMessage;
                        const isImportant = !!message.isImportant;
                        const isShared = !!message.sharedEntry;

                        // WhatsApp-style grouping logic
                        const previousMessage = index > 0 ? messages[index - 1] : null;
                        const nextMessage = index < messages.length - 1 ? messages[index + 1] : null;
                        
                        // Determine if this is the same sender as previous message
                        const isSameSenderAsPrevious = previousMessage && (
                          (isAdmin && previousMessage.isAdminMessage) ||
                          (!isAdmin && !previousMessage.isAdminMessage && message.userId?._id === previousMessage.userId?._id)
                        );
                        
                        // Determine if next message is from same sender
                        const isSameSenderAsNext = nextMessage && (
                          (isAdmin && nextMessage.isAdminMessage) ||
                          (!isAdmin && !nextMessage.isAdminMessage && message.userId?._id === nextMessage.userId?._id)
                        );
                        
                        // Only show avatar and name for first message in a group
                        const showAvatar = !isSameSenderAsPrevious;
                        const showName = !isAdmin && !isSameSenderAsPrevious;
                        
                        // Add grouping classes for styling
                        const isFirstInGroup = !isSameSenderAsPrevious;
                        const isLastInGroup = !isSameSenderAsNext;
                        const isMiddleInGroup = isSameSenderAsPrevious && isSameSenderAsNext;

                        return (
                          <React.Fragment key={message._id || index}>
                            {showDateDivider && (
                              <div className={styles.dateDivider}>
                                <span>{currentDate}</span>
                              </div>
                            )}
                            
                            <div className={`${styles.messageRow} ${isAdmin ? styles.messageRowAdmin : styles.messageRowUser} ${
                              isFirstInGroup ? styles.firstInGroup : ''
                            } ${isLastInGroup ? styles.lastInGroup : ''} ${
                              isMiddleInGroup ? styles.middleInGroup : ''
                            }`}>
                              {!isAdmin && showAvatar && (
                                <div className={styles.messageAvatar}>
                                  {message.userId?.name?.[0] || "U"}
                                </div>
                              )}
                              
                              {!isAdmin && !showAvatar && (
                                <div className={styles.messageAvatarPlaceholder}></div>
                              )}
                              
                              <div className={styles.messageContent}>
                                {showName && (
                                  <div className={styles.messageSender}>
                                    {message.userId?.name || "User"}
                                  </div>
                                )}
                                
                                {isShared && message.sharedEntry ? (
                                  <div 
                                    className={`${styles.messageBubble} ${styles.sharedBubble} ${isAdmin ? styles.bubbleAdmin : styles.bubbleUser}`}
                                  >
                                    <div className={styles.sharedTitle}>
                                      <FontAwesomeIcon icon={faShare} className={styles.shareIcon} />
                                      {message.sharedEntry.title}
                                    </div>
                                    {message.sharedEntry.body && (
                                      <>
                                        <div className={styles.sharedBody}>
                                          {formatSharedBody(message.sharedEntry.body)}
                                        </div>
                                        <span
                                          className={styles.readMoreLink}
                                          onClick={() => handleSharedEntryClick(message.sharedEntry)}
                                          title="View full blog"
                                        >
                                          Read More →
                                        </span>
                                      </>
                                    )}
                                    <div className={styles.messageTime}>
                                      {formatTime(message.createdAt)}
                                    </div>
                                  </div>
                                ) : isImportant ? (
                                  <div className={`${styles.messageBubble} ${styles.importantBubble} ${isAdmin ? styles.bubbleAdmin : styles.bubbleUser}`}>
                                    <div className={styles.importantHeader}>
                                      <FontAwesomeIcon icon={faExclamationCircle} />
                                      Important
                                    </div>
                                    <div className={styles.messageText}>{message.message}</div>
                                    <div className={styles.messageTime}>
                                      {formatTime(message.createdAt)}
                                    </div>
                                  </div>
                                ) : (
                                  <div className={`${styles.messageBubble} ${isAdmin ? styles.bubbleAdmin : styles.bubbleUser}`}>
                                    <div className={styles.messageText}>{message.message}</div>
                                    <div className={styles.messageTime}>
                                      {formatTime(message.createdAt)}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                <div className={styles.inputArea}>
                  <div className={styles.inputContainer}>
                    <input
                      type="text"
                      className={styles.messageInput}
                      value={chatText}
                      onChange={(e) => setChatText(e.target.value)}
                      onKeyDown={handleInputKeyDown}
                      placeholder="Type your message..."
                      disabled={sendingMessage}
                    />
                    <div className={styles.inputActions}>
                      <button
                        className={styles.actionBtn}
                        onClick={handleSendImportant}
                        disabled={sendingMessage || !chatText.trim()}
                        title="Send Important Message"
                      >
                        <FontAwesomeIcon icon={faExclamationCircle} />
                      </button>
                      <button
                        className={styles.actionBtn}
                        onClick={() => setShowShareModal(true)}
                        disabled={shareLoading}
                        title="Share Content"
                      >
                        <FontAwesomeIcon icon={faShare} />
                      </button>
                      <button
                        className={styles.sendBtn}
                        onClick={handleSendNormal}
                        disabled={sendingMessage || !chatText.trim()}
                      >
                        <FontAwesomeIcon icon={faPaperPlane} />
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faMessage} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
                <p>Select a chat room to start messaging</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Share Content Modal */}
      <Modal show={showShareModal} onHide={() => setShowShareModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Share Content</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <label style={{ fontWeight: 600 }}>Search Content</label>
            <input
              type="text"
              className="form-control"
              value={shareSearch}
              onChange={handleSearchChange}
              placeholder="Search by title..."
              autoComplete="off"
            />
            {shareSearchLoading && (
              <div style={{ textAlign: "center", padding: "1rem" }}>
                <img src={gearsGif} alt="Loading" width="30" />
              </div>
            )}
            {!shareSearchLoading && shareResults.length > 0 && (
              <div style={{
                maxHeight: 250,
                overflowY: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
              }}>
                {shareResults.map((entry) => (
                  <div
                    key={entry._id}
                    style={{
                      padding: 12,
                      cursor: "pointer",
                      backgroundColor: selectedShareEntry?._id === entry._id ? "#e0e7ff" : "white",
                      borderBottom: "1px solid #e2e8f0",
                      transition: "background 0.2s",
                    }}
                    onClick={() => handleSelectShareEntry(entry)}
                    onMouseEnter={(e) => {
                      if (selectedShareEntry?._id !== entry._id) {
                        e.currentTarget.style.backgroundColor = "#f8fafc";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedShareEntry?._id !== entry._id) {
                        e.currentTarget.style.backgroundColor = "white";
                      }
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{entry.title}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      {entry.mainCategory?.name} › {entry.subCategory?.name} › {entry.topic?.name}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!shareSearchLoading && shareSearch.trim().length >= 2 && shareResults.length === 0 && (
              <div style={{ textAlign: "center", padding: "1rem", color: "#94a3b8" }}>
                No content found
              </div>
            )}
            {selectedShareEntry && (
              <div style={{
                padding: 12,
                borderRadius: 8,
                backgroundColor: "#f0fdf4",
                border: "2px solid #86efac",
              }}>
                <div style={{ fontSize: 14, color: "#166534", fontWeight: 600 }}>
                  ✓ Selected: {selectedShareEntry.title}
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowShareModal(false)}
            disabled={shareLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSendShare}
            disabled={shareLoading || !selectedShareEntry}
          >
            {shareLoading ? "Sharing..." : "Share Content"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
